import { progress } from "@/components/knowledge/graphMotion";
import { pathwayOf } from "@/components/knowledge/graphData";

const INK = "#29243B";
const HALO = { paintOrder: "stroke", stroke: "#FFFFFF", strokeWidth: 4, strokeLinejoin: "round" };

const Label = ({ label, font, family, weight, color = INK, opacity = 1 }) => (
  <text textAnchor={label.anchor} fontFamily={family} fontSize={font} fontWeight={weight} fill={color} opacity={opacity}
    style={{ ...HALO, transition: "opacity .3s" }}>
    {label.lines.map((line, i) => (
      <tspan key={line} x={label.dx} y={label.dy + i * label.lh} dominantBaseline="middle">{line}</tspan>
    ))}
  </text>
);

const Hub = ({ n, L, breathe }) => {
  const p = pathwayOf(n.pathway);
  const Icon = p.icon;
  return (
    <>
      <circle r={n.r + 10 + breathe * 4} fill={p.fill} opacity={0.45} />
      <circle r={n.r} fill={p.fill} stroke={p.stroke} strokeWidth={1.6} />
      <Icon x={-L.icon / 2} y={-L.icon / 2} width={L.icon} height={L.icon} color={INK} strokeWidth={1.6} aria-hidden="true" />
      <Label label={n.label} font={L.hubFont} family="Fredoka, sans-serif" weight={600} />
    </>
  );
};

const Concept = ({ n, L, emphasis }) => {
  const p = pathwayOf(n.pathway);
  return (
    <>
      <circle r={n.r} fill="#FFFFFF" stroke={p.stroke} strokeWidth={2.2} />
      <circle r={n.r * 0.42} fill={p.fill} />
      <Label label={n.label} font={L.font} family="'Nunito Sans', sans-serif" weight={600} color="#3D3652" opacity={emphasis ? 1 : 0.7} />
    </>
  );
};

// Theme node: white disc with a coloured dot for every pathway it draws on.
// On the full map its label stays quiet until the theme is lit, hovered or selected (progressive disclosure).
const Project = ({ n, L, emphasis, lit }) => {
  const Icon = n.icon;
  return (
    <>
      <circle r={n.r + 5} fill="none" stroke={INK} strokeOpacity={0.18} strokeDasharray="2 5" strokeWidth={1.4} />
      <circle r={n.r} fill="#FFFFFF" stroke={INK} strokeOpacity={0.55} strokeWidth={1.4} />
      {n.pathways.map((id, i) => {
        const a = -Math.PI / 2 + (i * 2 * Math.PI) / n.pathways.length + Math.PI / 4;
        return <circle key={id} cx={Math.cos(a) * n.r} cy={Math.sin(a) * n.r} r={L.simple ? 4.5 : 3.6} fill={pathwayOf(id).stroke} stroke="#FFFFFF" strokeWidth={1.4} />;
      })}
      <Icon x={-L.icon * 0.42} y={-L.icon * 0.42} width={L.icon * 0.84} height={L.icon * 0.84} color={INK} strokeWidth={1.6} aria-hidden="true" />
      <Label label={n.label} font={L.font} family="Fredoka, sans-serif" weight={500}
        opacity={L.simple || emphasis ? 1 : 0.42 + lit * 0.58} />
    </>
  );
};

const describe = (n) =>
  n.kind === "hub" ? `${pathwayOf(n.pathway).title} pathway` : n.kind === "project" ? `${n.title} theme` : `${n.title}, ${pathwayOf(n.pathway).title}`;

export const GraphNodes = ({ graph, pos, t, focus, tour, hover, selected, reduce, onHover, onSelect }) => (
  <g>
    {graph.nodes.map((n) => {
      const appear = progress(t, n.start, 0.8);
      if (appear <= 0) return null;
      const breathe = reduce ? 0 : Math.sin(t * 0.9 + n.phase);
      const scale = appear * (n.kind === "concept" ? 1 : 1 + breathe * (n.kind === "hub" ? 0.03 : 0.02));
      const dim = focus && !focus.nodes.has(n.id);
      const { x, y } = pos[n.id];
      const activate = () => onSelect(n.id);
      const emphasis = !!focus?.nodes.has(n.id);
      const lit = tour.id === n.id ? tour.env : 0;
      return (
        <g key={n.id} transform={`translate(${x.toFixed(1)} ${y.toFixed(1)}) scale(${scale.toFixed(3)})`}
          opacity={dim ? 0.14 : appear} style={{ transition: "opacity .3s", cursor: "pointer", outline: "none" }}
          role="button" tabIndex={0} aria-label={describe(n)} aria-pressed={selected === n.id} data-testid={`kg-node-${n.id}`}
          onPointerEnter={(e) => e.pointerType === "mouse" && onHover(n.id)} onPointerLeave={() => onHover(null)}
          onFocus={() => onHover(n.id)} onBlur={() => onHover(null)}
          onClick={(e) => { e.stopPropagation(); activate(); }}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); activate(); } }}>
          {(hover === n.id || selected === n.id) && (
            <circle r={n.r + (n.kind === "concept" ? 7 : n.kind === "project" ? 9 : 16)} fill="none" stroke={INK} strokeWidth={1.4} strokeDasharray="3 4" />
          )}
          {n.kind === "hub" && <Hub n={n} L={graph.L} breathe={breathe} />}
          {n.kind === "concept" && <Concept n={n} L={graph.L} emphasis={emphasis} />}
          {n.kind === "project" && <Project n={n} L={graph.L} emphasis={emphasis} lit={focus ? 0 : lit} />}
        </g>
      );
    })}
  </g>
);
